import React, { useCallback, useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { AsyncSelectData } from "./AsyncSelectData";
import { IAsyncSelect } from "./IAsyncSelect";


const LOAD_MORE_INDICATOR_VALUE = "__load_more_indicator__";
const loadMoreIndicatorOption = new AsyncSelectData({
  title: "Loading more...",
  value: LOAD_MORE_INDICATOR_VALUE,
  enabled: false,
});

const AsyncSelect = React.forwardRef(
  (
    {
      className,
      onOpen,
      data,
      loadingData = true,
      placeholder = "",
      onItemSelected,
      hasError = false,
      label = undefined,
      defaultSelectOption = null,
      inputValueChanged = (_evt: any, _value: any) => {
        console.log("");
      },
      keyUp = (_evt) => {
        console.log("");
      },
      disabled = false,
      loadingMoreData = false,
      onLoadMore,
    }: IAsyncSelect,
    ref
  ) => {
    const LOAD_MORE_THRESHOLD_PX = 80;
    const LISTBOX_MAX_HEIGHT_PX = 320;
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<readonly AsyncSelectData[]>([]);
    const [selectedOption, setSelectedOption] =
      useState<AsyncSelectData | null>(null);
    const [defaultSelectedOption, setDefaultSelectedOption] = useState<
      AsyncSelectData | null | undefined
    >(null);
    const [defaultAlreadySet, setDefaultAlreadySet] = useState(false);
    const loadMoreRequestedRef = useRef(false);

    useEffect(() => {
      if (defaultSelectOption && !defaultSelectedOption) {
        setDefaultSelectedOption(defaultSelectOption);
      }
    }, [
      setDefaultSelectedOption,
      defaultSelectOption,
      defaultSelectedOption,
    ]);

    useEffect(() => {
      if (data && data.length) {
        setOptions(data);
      } else {
        setOptions([]);
      }
    }, [data]);

    useEffect(() => {
      if (open) {
        onOpen?.();
      }
    }, [open, onOpen]);

    useEffect(() => {
      if (!loadingMoreData) {
        loadMoreRequestedRef.current = false;
      }
    }, [loadingMoreData]);

    useEffect(() => {
      if (
        !selectedOption &&
        !defaultAlreadySet &&
        defaultSelectOption &&
        !defaultSelectedOption
      ) {
        setSelectedOption(defaultSelectOption);
        setDefaultAlreadySet(true);
      }
    }, [
      defaultSelectOption,
      selectedOption,
      defaultAlreadySet,
      defaultSelectedOption,
    ]);

    const selectedItemChanged = useCallback(
      (evt: any, value: any) => {
        if ((value as AsyncSelectData)?.value === LOAD_MORE_INDICATOR_VALUE) {
          return;
        }
        if (onItemSelected) {
          onItemSelected(value as AsyncSelectData);
          setSelectedOption(value as AsyncSelectData);
        }
      },
      [onItemSelected, setSelectedOption]
    );

    const handleListboxScroll = useCallback(
      (event: React.SyntheticEvent) => {
        const target = event.currentTarget as HTMLUListElement;
        const distanceToBottom =
          target.scrollHeight - target.scrollTop - target.clientHeight;
        const shouldLoadMore = distanceToBottom <= LOAD_MORE_THRESHOLD_PX;

        if (!shouldLoadMore) {
          loadMoreRequestedRef.current = false;
          return;
        }

        if (loadingData || loadingMoreData || loadMoreRequestedRef.current) {
          return;
        }

        loadMoreRequestedRef.current = true;
        onLoadMore?.();
      },
      [loadingData, loadingMoreData, onLoadMore]
    );

    const displayOptions = loadingMoreData
      ? [...options, loadMoreIndicatorOption]
      : options;

    return (
      <Autocomplete
        value={selectedOption}
        ref={ref}
        disabled={disabled}
        handleHomeEndKeys={true}
        filterOptions={(x) => x}
        onChange={selectedItemChanged}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: `${hasError ? "red" : ""}`,
          },
        }}
        
        className={`${className} w-full`}
        open={open}
        onOpen={() => {
          loadMoreRequestedRef.current = false;
          setOpen(true);
        }}
        onKeyUp={keyUp}
        onClose={() => {
          loadMoreRequestedRef.current = false;
          setOpen(false);
        }}
        defaultValue={defaultSelectedOption}
        onInputChange={(event, newInputValue) => {
          inputValueChanged?.(event, newInputValue);
        }}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) =>
          `${option.title} ${option.labelMarker ?? ""}`
        }
        options={displayOptions}
        loading={loadingData}
        slotProps={{
          popper: {
            sx: { zIndex: 1650 },
          },
        }}
        ListboxProps={{
          onScroll: handleListboxScroll,
          style: {
            maxHeight: LISTBOX_MAX_HEIGHT_PX,
            overflowY: "auto",
          },
        }}
        renderInput={(params) => (
          <TextField
            placeholder={placeholder}
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingData ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            label={label}
          />
        )}
        renderOption={(props, option) => {
          if (option.value === LOAD_MORE_INDICATOR_VALUE) {
            return (
              <li
                {...props}
                key={option.value}
                className="pointer-events-none"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  color: "#6b7280",
                  padding: "8px 12px",
                }}
              >
                <CircularProgress color="inherit" size={16} />
                <span>{option.title}</span>
              </li>
            );
          }

          if (!option.enabled) {
            props.onClick = (evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            };
          }
          return (
            <li
              className={`hover:!bg-gray-500 ${
                !option.enabled ? "pointer-events-none" : ""
              }`}
              style={{
                backgroundColor: option.enabled ? "" : "#e5e5e5",
                color: option.enabled ? "black" : "#acacac",
                maxWidth: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
              title={option.tooltip ?? option.title}
              {...props}
              key={option.value}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  flex: 1,
                }}
              >
                {option.title}
              </span>
              {option.labelMarker && (
                <div style={{ color: "red" }}>{option.labelMarker}</div>
              )}
            </li>
          );
        }}
      />
    );
  }
);

export { AsyncSelect, AsyncSelectData };


